import { Chip, Stack } from "@mui/material";
import { createColumnHelper, flexRender } from "@tanstack/react-table";
import { useMemo } from "react";
import { ActionNames } from "tng-address-collection-shared/actionNames.ts";
import { CARD_ERROR_SEVERITIES } from "tng-address-collection-shared/cardStatuses.ts";
import { fullName } from "tng-address-collection-shared/fullName.ts";
import {
  CardErrorType,
  CardErrorsItem,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { MemoDetailButton } from "../components/DetailButton.tsx";
import { MemoDetailClickCell } from "../components/DetailClickCell.tsx";
import { MailToCell } from "../components/MailToCell.tsx";
import { ProjectLinkCell } from "../components/ProjectLinkCell.tsx";
import { useTranslator } from "../i18n/translator.ts";
import { Meta } from "./useSignatureListColumns.ts";
import { useUserHasAction } from "./useUserHasAction.ts";

export const cardErrorTranslationKeys = {
  missingSenderManualDispatch: "cardErrorMissingSenderManual",
  missingSenderAutomaticDispatch: "cardErrorMissingSenderAutomatic",
  emailSenderManualDispatch: "cardErrorEmailSenderManual",
  xmasSenderAutomaticDispatch: "cardErrorXmasSenderAutomatic",
  senderUnknown: "cardErrorSenderUnknown",
  senderNotSigner: "cardErrorSenderNotSigner",
  duplicateEmail: "cardErrorDuplicateEmail",
  duplicateName: "cardErrorDuplicateName",
  contactDetailsInconsistent: "cardErrorContactDetailsInconsistent",
} as const satisfies Record<CardErrorType, string>;

export const cardErrorHintTranslationKeys = {
  missingSenderManualDispatch: "cardErrorMissingSenderManualHint",
  missingSenderAutomaticDispatch: "cardErrorMissingSenderAutomaticHint",
  emailSenderManualDispatch: "cardErrorEmailSenderManualHint",
  xmasSenderAutomaticDispatch: "cardErrorXmasSenderAutomaticHint",
  senderUnknown: "cardErrorSenderUnknownHint",
  senderNotSigner: "cardErrorSenderNotSignerHint",
  duplicateEmail: "cardErrorDuplicateEmailHint",
  duplicateName: "cardErrorDuplicateNameHint",
  contactDetailsInconsistent: "cardErrorContactDetailsInconsistentHint",
} as const satisfies Record<CardErrorType, string>;

export const cardSeverityTranslationKeys = {
  error: "cardStatusError",
  warning: "cardStatusWarning",
} as const satisfies Record<"error" | "warning", string>;

export const cardErrorTypes = Object.keys(
  cardErrorTranslationKeys,
) as CardErrorType[];

export const useCardErrorColumns = (
  handleDetailClick: (row: CardErrorsItem) => void,
) => {
  const translator = useTranslator();
  const columnHelper = useMemo(() => createColumnHelper<CardErrorsItem>(), []);
  const userHasAction = useUserHasAction();

  return useMemo(
    () =>
      [
        {
          column: columnHelper.accessor(fullName, {
            id: "name",
            header: () => translator("cardTableHeaderName"),
            cell: ({ row, getValue }) =>
              flexRender(MemoDetailClickCell, {
                handleClick: () => handleDetailClick(row.original),
                value: getValue(),
                userHasAction,
              }),
            size: 280,
            meta: {} satisfies Meta,
          }),
        },
        {
          column: columnHelper.accessor("email", {
            id: "email",
            header: () => translator("cardTableHeaderEmail"),
            cell: ({ getValue }) =>
              flexRender(MailToCell, { email: getValue() }),
            size: 280,
            meta: {} satisfies Meta,
          }),
        },
        {
          column: columnHelper.accessor("senderDisplayName", {
            id: "sender",
            header: () => translator("cardTableHeaderSender"),
            cell: ({ row, getValue }) =>
              flexRender(MemoDetailClickCell, {
                handleClick: () => handleDetailClick(row.original),
                value: getValue(),
                userHasAction,
              }),
            meta: {} satisfies Meta,
          }),
        },
        {
          column: columnHelper.accessor(
            (row) => ({ id: row.projectId, name: row.projectDisplayName }),
            {
              id: "project",
              header: () => translator("cardTableHeaderProject"),
              cell: ({ getValue }) => flexRender(ProjectLinkCell, getValue()),
              meta: {} satisfies Meta,
            },
          ),
        },
        {
          column: columnHelper.accessor("errors", {
            id: "errors",
            header: () => translator("cardErrorsColumn"),
            cell: ({ row, getValue }) =>
              flexRender(MemoDetailClickCell, {
                handleClick: () => handleDetailClick(row.original),
                value: (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {getValue().map((error) => {
                      const label = translator(cardErrorTranslationKeys[error]);
                      return (
                        <Chip
                          key={error}
                          label={label}
                          size="small"
                          color={CARD_ERROR_SEVERITIES[error]}
                          sx={{ fontWeight: 700 }}
                          aria-label={`${translator(cardSeverityTranslationKeys[CARD_ERROR_SEVERITIES[error]])}: ${label}`}
                        />
                      );
                    })}
                  </Stack>
                ),
                userHasAction,
              }),
            size: 440,
            meta: {} satisfies Meta,
          }),
        },
        {
          hide: !userHasAction(ActionNames.editCard),
          column: columnHelper.display({
            id: "edit",
            header: "",
            cell: (info) =>
              flexRender(MemoDetailButton, {
                onDetailClick: () => handleDetailClick(info.row.original),
              }),
            size: 70,
            meta: { align: "center" } satisfies Meta,
          }),
        },
      ]
        .filter((item) => !item.hide)
        .map((item) => item.column),
    [columnHelper, handleDetailClick, translator, userHasAction],
  );
};
